import { IcommerceUser, Icompany, IecommerceGuest, Istaff } from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../commerce-client';
import { Company } from '../defines/company/company.define';
import { Staff } from '../defines/user-related/staff.define';

/**
 * Retrieves the list of currency codes that the current user is permitted to use.
 * @returns {Promise<string[]>} A promise that resolves to an array of ISO 4217 currency codes.
 * @throws {Error} If the request fails or the user is not authenticated.
 * @example
 * const currencies = await getCurrencyList();
 * // Returns: ['USD', 'EUR', 'GBP']
 * @since 1.0.0
 */
export const getCurrencyList = (): Promise<string[]> => {
  const observer$ = CommerceClient.ehttp.makeGet<string[]>('/misc/permittable-currency-list');

  return lastValueFrom(observer$);
};

/**
 * Retrieves the current authenticated user's profile information including staff and company details.
 * @returns {Promise<{ staff?: Staff; company: Company }>} A promise that resolves to an object containing:
 * - staff: The staff member's details (if available)
 * - company: The company details
 * @throws {Error} If the request fails or the user is not authenticated.
 * @example
 * const { staff, company } = await myCurrentProfile();
 * console.log(`Welcome, ${staff?.name} from ${company.name}`);
 * @since 1.0.0
 */
export const myCurrentProfile = async(): Promise<{ staff?: Staff; company: Company }> => {
  const observer$ = CommerceClient.ehttp
    .makePost<{ staff?: Istaff; company: Icompany }>('/user-profile/my-current-profile', {});
  const res = await lastValueFrom(observer$);

  return {
    staff: res.staff ? new Staff(res.staff) : undefined,
    company: new Company(res.company)
  };
};

/**
 * Switches the current user's active company profile.
 * @param {string} companyId - The unique identifier of the company to switch to.
 * @returns {Promise<{ staff?: Staff; company: Company }>} A promise that resolves to an object containing:
 * - staff: The updated staff details (if available)
 * - company: The new active company details
 * @throws {Error} If the request fails, user is not authenticated, or company ID is invalid.
 * @example
 * // Switch to a different company
 * const { company } = await switchProfile('company-123');
 * console.log(`Switched to company: ${company.name}`);
 * @since 1.0.0
 */
export const switchProfile = async(companyId: string): Promise<{ staff?: Staff; company: Company }> => {
  const observer$ = CommerceClient.ehttp
    .makePost<{ staff: Istaff; company: Icompany }>('/user-profile/switch-profile', { companyId });
  const res = await lastValueFrom(observer$);

  return {
    staff: res.staff ? new Staff(res.staff) : undefined,
    company: new Company(res.company)
  };
};

/**
 * Retrieves the user's search history matching the given term.
 * @param {string} term - The search term to filter history by.
 * @returns {Promise<string[]>} A promise that resolves to an array of search history items.
 * @throws {Error} If the request fails or the user is not authenticated.
 * @example
 * const history = await getSearchHistory('product');
 * // Returns: ['product 1', 'product catalog']
 * @since 1.0.0
 */
export const getSearchHistory = (term: string): Promise<string[]> => {
  const observer$ = CommerceClient.ehttp
    .makeGet<string[]>(`/user-profile/get-search-history/${encodeURIComponent(term)}`);

  return lastValueFrom(observer$);
};

/**
 * Clears the current user's search history.
 * @returns {Promise<void>} A promise that resolves when the operation is complete.
 * @throws {Error} If the request fails or the user is not authenticated.
 * @example
 * await clearSearchHistory();
 * console.log('Search history cleared');
 * @since 1.0.0
 */
export const clearSearchHistory = (): Promise<void> => {
  const observer$ = CommerceClient.ehttp.makePost('/user-profile/clear-search-history', {});

  return lastValueFrom(observer$);
};

/**
 * Retrieves the buyer's search history matching the given term.
 * @param {string} term - The search term to filter history by.
 * @returns {Promise<string[]>} A promise that resolves to an array of buyer search history items.
 * @throws {Error} If the request fails or the user is not authenticated.
 * @example
 * const buyerHistory = await getBuyerSearchHistory('order');
 * // Returns: ['order status', 'order history']
 * @since 1.0.0
 */
export const getBuyerSearchHistory = (term: string): Promise<string[]> => {
  const observer$ = CommerceClient.ehttp
    .makeGet<string[]>(`/buyer/get-buyer-search-history/${encodeURIComponent(term)}`);

  return lastValueFrom(observer$);
};

/**
 * Clears the current buyer's search history.
 * @returns {Promise<void>} A promise that resolves when the operation is complete.
 * @throws {Error} If the request fails or the user is not authenticated.
 * @example
 * await clearBuyerSearchHistory();
 * console.log('Buyer search history cleared');
 * @since 1.0.0
 */
export const clearBuyerSearchHistory = (): Promise<void> => {
  const observer$ = CommerceClient.ehttp.makePost('/buyer/clear-buyer-search-history', {});

  return lastValueFrom(observer$);
};

/**
 * Retrieves the guest user information.
 * @returns {Promise<EcommerceGuest>} A promise that resolves to the guest user information.
 * @throws {Error} If the request fails or the user is not authenticated.
 * @example
 * const guestInfo = await getGuestInfo();
 * // Returns: { name: 'Guest User', email: 'guest@example.com' }
 * @since 1.0.0
 */
export const getGuestInfo = (): Promise<IecommerceGuest<string, IcommerceUser>> => {
  const observer$ = CommerceClient.ehttp.makeGet<IecommerceGuest<string, IcommerceUser>>('/guest-session/info');

  return lastValueFrom(observer$);
};

/**
 * Resets the guest user session.
 * @returns {Promise<void>} A promise that resolves when the operation is complete.
 * @throws {Error} If the request fails or the user is not authenticated.
 * @example
 * await resetGuestSession();
 * console.log('Guest session reset');
 * @since 1.0.0
 */
export const resetGuestSession = (): Promise<void> => {
  const observer$ = CommerceClient.ehttp.makePost('/guest-session/reset', {});

  return lastValueFrom(observer$);
};

/**
 * Checks if the guest user is linked to an authenticated user.
 * @returns {Promise<{ isLinked: boolean }>} A promise that resolves to an object containing:
 * - isLinked: A boolean indicating whether the guest user is linked to an authenticated user
 * @throws {Error} If the request fails or the user is not authenticated.
 * @example
 * const { isLinked } = await isGuestLinkedToAuthUser();
 * console.log(`Guest user is linked to auth user: ${isLinked}`);
 * @since 1.0.0
 */
export const isGuestLinkedToAuthUser = (): Promise<{ isLinked: boolean }> => {
  const observer$ = CommerceClient.ehttp.makePost<{ isLinked: boolean }>(
    '/guest-session/is-linked-to-auth-user',
    {}
  );

  return lastValueFrom(observer$);
};

